import { type Accessor, createSignal, For, type JSX, onCleanup, Show } from 'solid-js'

/**
 * Alto de cada tarjeta más su separación, en píxeles.
 *
 * La virtualización necesita saber cuánto mide una fila para calcular cuáles
 * caben en pantalla, así que **todas las tarjetas deben medir exactamente esto**.
 * Si cambias el contenido de la tarjeta, ajusta este número o la lista se
 * descuadra al desplazarse.
 */
export const KANBAN_ROW_HEIGHT = 112

/** Filas de más que se pintan por arriba y por abajo, para que no se vea el hueco al desplazar. */
const MARGEN = 3

interface KanbanColumnPropsType<T> {
  items: T[]
  /** Se pinta por cada tarjeta visible. */
  children: (item: T, index: Accessor<number>) => JSX.Element
  /** Qué mostrar cuando la columna está vacía. */
  empty?: JSX.Element
}

/**
 * Columna del tablero que sólo monta en el DOM las tarjetas visibles.
 *
 * Con 500 clientes repartidos en las columnas, montarlas todas eran miles de
 * nodos y el tablero se arrastraba; así cuesta lo mismo que con veinte.
 *
 * La virtualización está escrita a mano y no con `@solid-primitives/virtual`
 * porque su componente fija anchos por estilo inline (las tarjetas se quedaban
 * más estrechas que la columna, con barra horizontal propia) y su versión sin
 * interfaz no reaccionaba al cambiar la lista: al filtrar seguían viéndose las
 * tarjetas anteriores. Aquí todo depende de señales, así que se recalcula solo.
 */
export function KanbanColumn<T>(props: KanbanColumnPropsType<T>): JSX.Element {
  const [alto, setAlto] = createSignal(0)
  const [desplazamiento, setDesplazamiento] = createSignal(0)

  const medir = (el: HTMLDivElement) => {
    setAlto(el.clientHeight)
    // El alto cambia al redimensionar la ventana o al plegar el menú lateral.
    const observer = new ResizeObserver(() => setAlto(el.clientHeight))
    observer.observe(el)
    onCleanup(() => observer.disconnect())
  }

  const total = () => props.items.length
  const desde = () => Math.max(0, Math.floor(desplazamiento() / KANBAN_ROW_HEIGHT) - MARGEN)
  const hasta = () => Math.min(total(), Math.ceil((desplazamiento() + alto()) / KANBAN_ROW_HEIGHT) + MARGEN)
  const visibles = () => props.items.slice(desde(), hasta())

  return (
    <div class="flex-1 min-h-0 overflow-hidden">
      <Show when={total() > 0} fallback={<div class="p-2">{props.empty}</div>}>
        <div ref={medir} onScroll={e => setDesplazamiento(e.currentTarget.scrollTop)} class="h-full overflow-y-auto overflow-x-hidden px-2 pt-2">
          {/* Ocupa el alto de todas las filas: es lo que da la barra de desplazamiento real. */}
          <div style={{ height: `${total() * KANBAN_ROW_HEIGHT}px` }} class="relative w-full">
            <div style={{ transform: `translateY(${desde() * KANBAN_ROW_HEIGHT}px)` }} class="absolute inset-x-0 top-0">
              <For each={visibles()}>{(item, i) => props.children(item, () => desde() + i())}</For>
            </div>
          </div>
        </div>
      </Show>
    </div>
  )
}

export default KanbanColumn
